// src/pages/PromotionsPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Segmented,
  Input,
  Button,
  Alert,
  Modal,
  Form,
  DatePicker,
  Select,
  InputNumber,
  Divider,
  message,
  Tooltip,
} from "antd";

import {
  ReloadOutlined,
  SearchOutlined,
  PlusOutlined,
  CopyOutlined,
  GiftOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_ORDER = { Active: 0, Upcoming: 1, Expired: 2, None: 3 };
const STATUS_COLOR = {
  Active: "green",
  Upcoming: "blue",
  Expired: "red",
  None: undefined,
};

/**
 * Recommended: use ONE stable endpoint.
 * Ensure backend has: GET /api/RoomTypes/hotel/{hotelId}
 */
const ROOMTYPES_ENDPOINT = (hotelId) => `/RoomTypes/hotel/${hotelId}`;

const PromotionsPage = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const location = useLocation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("All");
  const [searchText, setSearchText] = useState("");

  // Create Promotion modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  const [roomTypes, setRoomTypes] = useState([]); // [{roomTypeId, typeName}]
  const [promoCode, setPromoCode] = useState("");

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "N/A";
    const datePart = d.toLocaleDateString("en-US");
    const timePart = d.toLocaleTimeString("en-US");
    return `${datePart} ${timePart}`;
  };

  const formatMoney = (currency, value) => {
    const ccy = (currency || "BWP").toUpperCase();
    const n = Number(value ?? 0);
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: ccy,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${ccy} ${n.toFixed(2)}`;
    }
  };

  const normalizePromo = (p) => {
    const roomType = p.roomType ?? p.RoomType ?? "";
    const standardRate = p.standardRate ?? p.StandardRate ?? 0;
    const discountedRate = p.discountedRate ?? p.DiscountedRate ?? 0;
    const currency = p.currency ?? p.Currency ?? "BWP";
    const discountStart = p.discountStart ?? p.DiscountStart ?? null;
    const discountEnd = p.discountEnd ?? p.DiscountEnd ?? null;

    const discountStatus = p.discountStatus ?? p.DiscountStatus ?? "None";
    const isDiscountActive =
      p.isDiscountActive ??
      p.IsDiscountActive ??
      String(discountStatus).toLowerCase() === "active";

    const netRate = p.netRate ?? p.NetRate ?? null;
    const vatAmount = p.vatAmount ?? p.VatAmount ?? null;
    const totalPerNight = p.totalPerNight ?? p.TotalPerNight ?? null;

    return {
      roomType,
      standardRate: Number(standardRate ?? 0),
      discountedRate: Number(discountedRate ?? 0),
      currency,
      discountStart,
      discountEnd,
      discountStatus: discountStatus || "None",
      isDiscountActive: !!isDiscountActive,
      netRate: netRate == null ? null : Number(netRate),
      vatAmount: vatAmount == null ? null : Number(vatAmount),
      totalPerNight: totalPerNight == null ? null : Number(totalPerNight),
    };
  };

  const fetchPromotions = useCallback(async () => {
    if (!hotelId) {
      setError("Hotel ID is not available.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await axiosInstance.get(`/Price/discounted/hotel/${hotelId}`);
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list.map(normalizePromo));
    } catch (err) {
      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load promotions";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  const fetchRoomTypes = useCallback(async () => {
    if (!hotelId) return;

    try {
      const res = await axiosInstance.get(ROOMTYPES_ENDPOINT(hotelId));
      const list = Array.isArray(res.data) ? res.data : [];

      const normalized = list
        .map((x) => ({
          roomTypeId: x.roomTypeId ?? x.RoomTypeId ?? x.id ?? x.Id,
          typeName: x.typeName ?? x.TypeName ?? x.name ?? x.Name,
        }))
        .filter(
          (x) =>
            x.roomTypeId != null && String(x.typeName || "").trim() !== ""
        );

      setRoomTypes(normalized);
    } catch (err) {
      // If room types endpoint is missing, we fallback to promo row names for display-only.
      const fallback = Array.from(
        new Set(rows.map((r) => r.roomType).filter(Boolean))
      ).map((name, idx) => ({ roomTypeId: `name-${idx}`, typeName: name }));

      setRoomTypes(fallback);

      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load room types";

      // Keep this as a non-blocking warning; creation will still error if IDs are missing.
      console.warn("[Promotions] RoomTypes fetch failed:", msg);
    }
  }, [hotelId, rows]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const filteredSorted = useMemo(() => {
    const s = searchText.trim().toLowerCase();

    const matchesStatus = (row) => {
      if (statusFilter === "All") return true;
      return String(row.discountStatus || "None") === statusFilter;
    };

    const matchesSearch = (row) => {
      if (!s) return true;
      return String(row.roomType || "").toLowerCase().includes(s);
    };

    const statusKey = (row) => STATUS_ORDER[row.discountStatus] ?? 99;

    const endTs = (row) => {
      if (!row.discountEnd) return Number.POSITIVE_INFINITY;
      const t = new Date(row.discountEnd).getTime();
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };

    return [...rows]
      .filter((r) => matchesStatus(r) && matchesSearch(r))
      .sort((a, b) => {
        const ak = statusKey(a);
        const bk = statusKey(b);
        if (ak !== bk) return ak - bk;
        return endTs(a) - endTs(b);
      })
      .map((r, idx) => ({
        ...r,
        _rowId: `${r.roomType}-${r.discountStart}-${r.discountEnd}-${idx}`,
      }));
  }, [rows, statusFilter, searchText]);

  const columns = useMemo(
    () => [
      {
        title: "Room Type",
        dataIndex: "roomType",
        key: "roomType",
        sorter: (a, b) => String(a.roomType).localeCompare(String(b.roomType)),
        render: (v) => <Text strong>{v || "Unknown"}</Text>,
      },
      {
        title: "Standard Rate",
        dataIndex: "standardRate",
        key: "standardRate",
        align: "right",
        sorter: (a, b) => (a.standardRate ?? 0) - (b.standardRate ?? 0),
        render: (_, r) => formatMoney(r.currency, r.standardRate),
      },
      {
        title: "Promotion Start",
        dataIndex: "discountStart",
        key: "discountStart",
        render: (v) => formatDateTime(v),
      },
      {
        title: "Promotion End",
        dataIndex: "discountEnd",
        key: "discountEnd",
        render: (v) => formatDateTime(v),
      },
      {
        title: "Discount (Now)",
        dataIndex: "discountedRate",
        key: "discountedRate",
        align: "right",
        sorter: (a, b) => (a.discountedRate ?? 0) - (b.discountedRate ?? 0),
        render: (_, r) => {
          const pct = Number(r.discountedRate ?? 0);
          const net = r.standardRate * (1 - pct / 100);

          return (
            <Space direction="vertical" size={0} style={{ textAlign: "right" }}>
              <Text strong>{pct.toFixed(2)}%</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Net: {formatMoney(r.currency, net)}
              </Text>
            </Space>
          );
        },
      },
      {
        title: "Total / Night",
        key: "totalPerNight",
        align: "right",
        render: (_, r) => {
          if (r.totalPerNight != null)
            return formatMoney(r.currency, r.totalPerNight);

          const pct = Number(r.discountedRate ?? 0);
          const net = r.standardRate * (1 - pct / 100);
          return <Text type="secondary">{formatMoney(r.currency, net)}</Text>;
        },
      },
      {
        title: "Status",
        dataIndex: "discountStatus",
        key: "discountStatus",
        sorter: (a, b) =>
          (STATUS_ORDER[a.discountStatus] ?? 99) -
          (STATUS_ORDER[b.discountStatus] ?? 99),
        defaultSortOrder: "ascend",
        render: (statusRaw, r) => {
          const status = statusRaw || "None";
          const color = STATUS_COLOR[status] ?? undefined;

          return (
            <Space>
              <Tag color={color}>{status}</Tag>
              {r.isDiscountActive ? <Tag color="green">Live</Tag> : null}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchText]
  );

  const generatePromoCode = () => {
    const weekend = (() => {
      const d = new Date().getDay();
      return d === 5 || d === 6 || d === 0;
    })();

    const prefix = weekend ? "WKND" : "PROMO";
    const stamp = dayjs().format("DDHH");
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `${prefix}-${stamp}${rand}`;
  };

  const openCreate = useCallback(async () => {
    setPromoCode(generatePromoCode());
    setCreateOpen(true);

    // Load room types for selection
    await fetchRoomTypes();

    // Defaults: today -> +2 days
    const start = dayjs();
    const end = dayjs().add(2, "day");

    form.setFieldsValue({
      scope: "ALL", // ALL or ONE
      roomTypeId: undefined,
      discount: 10,
      range: [start, end],
    });
  }, [fetchRoomTypes, form]);

  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  // Auto-open if query param create=1 is present
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const shouldCreate = qs.get("create") === "1";
    if (!shouldCreate) return;

    openCreate().finally(() => {
      qs.delete("create");
      const next = `${location.pathname}${
        qs.toString() ? `?${qs.toString()}` : ""
      }`;
      navigate(next, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success("Copied to clipboard");
    } catch {
      message.error("Copy failed");
    }
  };

  const buildGuestMessage = (vals) => {
    const [start, end] = vals?.range || [];
    const startTxt = start ? start.format("ddd, DD MMM YYYY HH:mm") : "—";
    const endTxt = end ? end.format("ddd, DD MMM YYYY HH:mm") : "—";
    const scope = vals?.scope;

    const roomName =
      scope === "ALL"
        ? "all room types"
        : roomTypes.find((x) => String(x.roomTypeId) === String(vals?.roomTypeId))
            ?.typeName || "selected room type";

    const hotelName = authData?.hotelName || "our hotel";

    return `Weekend Special at ${hotelName}.
Room offer: ${roomName}
Discount: ${Number(vals?.discount || 0).toFixed(0)}%
Valid: ${startTxt} to ${endTxt}
Promotion code: ${promoCode}

Reply to book or use our booking link.`;
  };

  /**
   * Uses backend scope endpoint:
   * POST /api/Price/SetDiscountScope
   *
   * roomTypeId null => apply to all room types for hotel
   * roomTypeId number => apply to one room type
   */
  const applyDiscountScope = async ({ roomTypeId, discount, startIso, endIso }) => {
    // If roomTypeId is a fallback string (name-idx), we cannot apply server-side.
    if (roomTypeId != null && String(roomTypeId).startsWith("name-")) {
      throw new Error(
        "RoomTypeId is not available. Please ensure your RoomTypes endpoint returns IDs."
      );
    }

    const payload = {
      hotelId: Number(hotelId),
      roomTypeId: roomTypeId == null ? null : Number(roomTypeId),
      discount: Number(discount),
      startTime: startIso, // ISO UTC
      endTime: endIso,     // ISO UTC
    };

    await axiosInstance.post(`/Price/SetDiscountScope`, payload);
  };

  const onSubmitCreate = async () => {
    if (!hotelId) {
      message.error("Hotel ID is not available.");
      return;
    }

    try {
      const vals = await form.validateFields();

      const [start, end] = vals.range || [];
      if (!start || !end) {
        message.error("Please select a promotion start and end date.");
        return;
      }

      // Fix: do not use isSameOrBefore (plugin). Use numeric compare.
      if (end.valueOf() <= start.valueOf()) {
        message.error("Promotion end must be after start.");
        return;
      }

      const discount = Number(vals.discount || 0);
      if (!(discount > 0 && discount <= 100)) {
        message.error("Discount must be between 1 and 100.");
        return;
      }

      const startIso = start.toDate().toISOString();
      const endIso = end.toDate().toISOString();

      setSaving(true);

      if (vals.scope === "ALL") {
        // One call, server applies to all room types
        await applyDiscountScope({ roomTypeId: null, discount, startIso, endIso });
      } else {
        if (!vals.roomTypeId) {
          message.error("Please select a room type.");
          return;
        }
        await applyDiscountScope({
          roomTypeId: vals.roomTypeId,
          discount,
          startIso,
          endIso,
        });
      }

      message.success("Promotion applied successfully.");
      await fetchPromotions();
      setCreateOpen(false);
    } catch (e) {
      const msg =
        e?.response?.data?.Message ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to apply promotion";
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Breadcrumb />

      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-end",
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Promotions
            </Title>
            <Text type="secondary">Manage promotional offers here.</Text>
          </div>

          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              disabled={!hotelId}
            >
              Create Promotion
            </Button>

            <Button
              icon={<ReloadOutlined />}
              onClick={fetchPromotions}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>

        {error ? (
          <Alert
            type="error"
            showIcon
            message="Failed to load promotions"
            description={error}
          />
        ) : null}

        <Card variant="outlined" styles={{ body: { padding: 12 } }}>
          <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
            <Space wrap>
              <Segmented
                value={statusFilter}
                onChange={setStatusFilter}
                options={["All", "Active", "Expired", "Upcoming", "None"]}
              />

              <Input
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search room type..."
                prefix={<SearchOutlined />}
                style={{ width: 260 }}
              />
            </Space>
          </Space>

          <div style={{ marginTop: 12 }}>
            <Table
              size="middle"
              rowKey={(r) => r._rowId}
              loading={loading}
              columns={columns}
              dataSource={filteredSorted}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              scroll={{ x: 1100 }}
            />
          </div>

          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Status is computed by the API (Active/Upcoming/Expired/None).
              “Discount (Now)” shows the effective discount currently active; expired/upcoming discounts should display 0%.
            </Text>
          </div>
        </Card>
      </Space>

      {/* Create Promotion Modal */}
      <Modal
        open={createOpen}
        onCancel={closeCreate}
        title={
          <Space>
            <GiftOutlined />
            <span>Create Promotion</span>
          </Space>
        }
        okText="Apply Promotion"
        onOk={onSubmitCreate}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="scope"
            label="Room Type Scope"
            rules={[{ required: true, message: "Select scope" }]}
          >
            <Select
              options={[
                { value: "ALL", label: "All Room Types" },
                { value: "ONE", label: "Select One Room Type" },
              ]}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(p, c) => p.scope !== c.scope}>
            {({ getFieldValue }) =>
              getFieldValue("scope") === "ONE" ? (
                <Form.Item
                  name="roomTypeId"
                  label="Room Type"
                  rules={[{ required: true, message: "Select a room type" }]}
                >
                  <Select
                    placeholder="Choose room type..."
                    options={roomTypes.map((rt) => ({
                      value: rt.roomTypeId,
                      label: rt.typeName,
                    }))}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="discount"
            label="Discount (%)"
            rules={[{ required: true, message: "Enter discount" }]}
          >
            <InputNumber min={1} max={100} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="range"
            label="Promotion Window"
            rules={[{ required: true, message: "Select dates" }]}
          >
            <RangePicker showTime style={{ width: "100%" }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>

          <Divider style={{ margin: "10px 0" }} />

          <Form.Item label="Promotion Code (share with guest list)">
            <Space.Compact style={{ width: "100%" }}>
              <Input value={promoCode} readOnly />
              <Tooltip title="Copy code">
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(promoCode)}
                />
              </Tooltip>
            </Space.Compact>

            <div style={{ marginTop: 8 }}>
              <Button
                type="default"
                block
                icon={<CopyOutlined />}
                onClick={async () => {
                  const vals = form.getFieldsValue();
                  await copyToClipboard(buildGuestMessage(vals));
                }}
              >
                Copy guest message
              </Button>

              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginTop: 8 }}
              >
                Note: With the current pricing model, the discount applies during the selected window. The code is for marketing unless you add code validation at booking.
              </Text>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PromotionsPage;
